import type { ReactNode } from 'react';
import { Box, Chip, TableSortLabel, Tooltip, Typography } from '@mui/material';
import { fullDateTime, shortDateTime } from '../../shared/dateTime';
import { plNumber } from '../../shared/number';
import type {
  AfterOrder,
  CostLine,
  CostSplit,
  Feasibility,
  FeasibilityGrade,
  WindowReserve,
} from './suggestionsApi';

/**
 * Rekomendacje według makiety FTL p. Mariusza (13.10, pkt 14; Etap 1 —
 * decyzja Bartka 15.10.2026), wspólne dla obu kierunków: pojazdy dla
 * zlecenia (`VehicleSuggestions`) i zlecenia dla pojazdu
 * (`fleet/CarOrderSuggestions`) — ta sama treść tymi samymi słowami.
 *
 * - **wykonalność** (przełącznik `recommendationFeasibility`) — kategoria
 *   z API (`user-offers/feasibility.ts`): przepisane werdykty „zdąży”
 *   i „dowiezie” plus zapas do okna; wykonalne na górze, w grupie po
 *   dolocie. To nie „ocena pary 0–10” (`PairScore.tsx`);
 * - **zapas do okna** — osobna kolumna obok „zapasu czasu pracy”;
 * - **po zleceniu**, rozbicie kosztu, autor wariantu, lista pominiętych
 *   i godzina przeliczenia (przełącznik `recommendationDetails`);
 * - **sortowanie kliknięciem nagłówka** (przełącznik
 *   `recommendationColumnSort`) — tylko widok pokazanej listy.
 *
 * Nic tu nie liczymy poza kolejnością widoku — liczby przychodzą z API.
 * Bez dopracowywania kolorów (ustalenie 13.10).
 */

export const FEATURE_FEASIBILITY = 'recommendationFeasibility';
export const FEATURE_DETAILS = 'recommendationDetails';
export const FEATURE_COLUMN_SORT = 'recommendationColumnSort';

const NOWRAP = { whiteSpace: 'nowrap' } as const;

// ── Wykonalność ───────────────────────────────────────────────────────

/** Kolejność grup: wykonalne na górze (jak `FEASIBILITY_ORDER` w API). */
export const FEASIBILITY_ORDER: Record<FeasibilityGrade, number> = {
  fits: 0,
  tight: 1,
  unknown: 2,
  late: 3,
};

const FEASIBILITY_COLOR: Record<FeasibilityGrade, 'success' | 'warning' | 'default' | 'error'> = {
  fits: 'success',
  tight: 'warning',
  unknown: 'default',
  late: 'error',
};

/** Dymek przy wykonalności: powody z API, próg i czym to nie jest. */
export function feasibilityTooltip(f: Feasibility): string {
  return [
    `Wykonalność: ${f.label}${f.reasons.length > 0 ? ` — ${f.reasons.join('; ')}` : ''}.`,
    `„Ciasny dolot”: zapas do początku okna załadunku poniżej ${plNumber(f.tightThresholdH)} h (parametr rekomendacji, nasze założenie).`,
    'Kategoria z werdyktów „zdąży” i „dowiezie” (szacunek) — nikogo nie wyklucza; na liście wykonalne są na górze, w grupie po dolocie.',
  ].join(' ');
}

/** Czip wykonalności z powodem w dymku. Brak pola — nic. */
export function FeasibilityChip({ feasibility }: { feasibility?: Feasibility | null }) {
  if (!feasibility) return null;
  return (
    <Tooltip title={feasibilityTooltip(feasibility)}>
      <Chip
        size="small"
        variant="outlined"
        color={FEASIBILITY_COLOR[feasibility.grade]}
        label={feasibility.label}
        data-testid="feasibility"
        data-grade={feasibility.grade}
        sx={{ mt: 0.25 }}
      />
    </Tooltip>
  );
}

/**
 * Wykonalne na górze, w grupie kolejność z API (po dolocie) — sortowanie
 * stabilne. Wiersz bez wykonalności (starsze API) — jak „brak danych”;
 * gdy żaden jej nie ma, kolejność bez zmian.
 */
export function byFeasibility<T extends { feasibility?: Feasibility | null }>(rows: readonly T[]): T[] {
  if (!rows.some((r) => r.feasibility)) return [...rows];
  return rows
    .map((row, index) => ({ row, index, rank: FEASIBILITY_ORDER[row.feasibility?.grade ?? 'unknown'] }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((x) => x.row);
}

// ── Zapas do okna ─────────────────────────────────────────────────────

export const WINDOW_RESERVE_TOOLTIP =
  'Zapas do okna: ile godzin zostaje od szacowanego przyjazdu na załadunek do początku okna załadunku (wyjazd, gdy auto i kierowca są wolni, dolot z przerwami i odpoczynkiem). Ujemny — przyjazd po początku okna. To co innego niż „Zapas czasu pracy”, czyli czas pracy kierowcy, który zostanie mu po rozładunku. Szacunek.';

/** „+2,5 h” / „−1,5 h” — zapas ze znakiem (minus typograficzny). */
export function signedHours(h: number): string {
  if (h === 0) return '0 h';
  return h < 0 ? `−${plNumber(-h)} h` : `+${plNumber(h)} h`;
}

/**
 * Komórka „Zapas do okna”: liczba ze znakiem, przyjazd w dymku; poniżej progu
 * „ciasnego dolotu” — kolor ostrzeżenia. Bez liczby — „—” z powodem.
 */
export function WindowReserveCell({
  reserve,
  thresholdH,
}: {
  reserve?: WindowReserve | null;
  thresholdH?: number | null;
}) {
  if (!reserve) return <>—</>;
  if (reserve.hours === null) {
    return (
      <Tooltip title={`Nie liczymy: ${reserve.reason ?? 'brak danych'}.`}>
        <Typography variant="body2" component="span" data-testid="window-reserve-empty">
          —
        </Typography>
      </Tooltip>
    );
  }
  const tight = typeof thresholdH === 'number' && reserve.hours < thresholdH;
  return (
    <Tooltip title={`Szacowany przyjazd na załadunek ${fullDateTime(reserve.arrivalAt)}. ${WINDOW_RESERVE_TOOLTIP}`}>
      <Typography
        variant="body2"
        sx={NOWRAP}
        color={reserve.hours < 0 ? 'error.main' : tight ? 'warning.main' : undefined}
        data-testid="window-reserve-hours"
      >
        {signedHours(reserve.hours)}
      </Typography>
    </Tooltip>
  );
}

// ── Po zleceniu ───────────────────────────────────────────────────────

/** „po zleceniu wolny ok. 17.10, 12:30 · DE BERLIN” pod rozładunkiem. */
export function AfterOrderLine({ afterOrder }: { afterOrder?: AfterOrder | null }) {
  if (!afterOrder) return null;
  const place = afterOrder.place ? ` · ${afterOrder.place}` : '';
  if (afterOrder.freeAt === null) {
    return (
      <Tooltip title={`Nie wiemy, kiedy pojazd będzie wolny: ${afterOrder.reason ?? 'brak danych'}.`}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="after-order">
          {`po zleceniu: —${place}`}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={`Po zleceniu pojazd będzie wolny ok. ${fullDateTime(afterOrder.freeAt)}${
        afterOrder.place ? ` w miejscu rozładunku (${afterOrder.place})` : ''
      } — koniec rozładunku tym samym rachunkiem co „Rozładunek”, bez ulg. Szacunek.`}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="after-order">
        {/* Łamie się po „wolny”, nie w dacie — inaczej cała linia (ok. 180 px)
            wyznaczała szerokość kolumny „Rozładunek” i tabela nie mieściła się
            przy 1366 px (integracja Etapu 1, 15.10.2026). */}
        {'po zleceniu wolny '}
        <span style={NOWRAP}>{shortDateTime(afterOrder.freeAt)}</span>
        {place}
      </Typography>
    </Tooltip>
  );
}

// ── Koszt po składowych ───────────────────────────────────────────────

const zl = (v: number | null | undefined) => (v === null || v === undefined ? 'nieznane' : `${v.toLocaleString('pl-PL')} zł`);

/** „paliwo 120 zł, kierowca 80 zł, ciągnik 50 zł, opłaty drogowe 30 zł = ~280 zł”. */
export function costLineText(l: CostLine): string {
  const parts = [
    `paliwo ${zl(l.fuelPln)}`,
    `kierowca ${zl(l.driverPln)}`,
    `ciągnik ${zl(l.tractorPln)}`,
    ...(l.tollsPln !== undefined ? [`opłaty drogowe ${zl(l.tollsPln)}`] : []),
  ];
  return `${parts.join(', ')} = ${l.totalPln === null ? 'nieznany' : `~${zl(l.totalPln)}`}`;
}

/** Zdania do dymka marży: dolot, trasa zlecenia, razem. */
export function costSplitSentences(split?: CostSplit | null): string[] {
  if (!split) return [];
  return [
    `Koszt dolotu: ${costLineText(split.access)}.`,
    split.route
      ? `Koszt trasy zlecenia: ${costLineText(split.route)}.`
      : 'Koszt trasy zlecenia nieznany (brak dystansu trasy).',
    ...(split.total ? [`Razem: ${costLineText(split.total)}.`] : []),
  ];
}

// ── Sortowanie kliknięciem nagłówka ───────────────────────────────────

export type RecommendationSortKey =
  | 'feasibility'
  | 'access'
  | 'windowReserve'
  | 'workReserve'
  | 'margin'
  | 'delivery'
  | 'freeFrom';

/** Wybrana kolumna; `reversed` — drugie kliknięcie (od najgorszych). */
export interface RecommendationSort {
  key: RecommendationSortKey;
  reversed: boolean;
}

/** Kierunek „od najlepszych”: rosnąco (mniej = lepiej) czy malejąco. */
const BEST_ASCENDING: Record<RecommendationSortKey, boolean> = {
  feasibility: true,
  access: true,
  windowReserve: false,
  workReserve: false,
  margin: false,
  delivery: true,
  freeFrom: true,
};

/** Pierwsze kliknięcie — od najlepszych, drugie — odwrotnie, trzecie — kolejność domyślna. */
export function nextSort(current: RecommendationSort | null, key: RecommendationSortKey): RecommendationSort | null {
  if (current?.key !== key) return { key, reversed: false };
  return current.reversed ? null : { key, reversed: true };
}

/** Wiersz rekomendacji — pola wspólne dla obu kierunków (kształt z API). */
export interface SortableRecommendation {
  feasibility?: Feasibility | null;
  accessRoute?: { km: number | null } | null;
  accessDistance?: { roadKm: number } | null;
  windowReserve?: WindowReserve | null;
  workReserve?: { hours: number | null } | null;
  margin?: { pln: number | null } | null;
  delivery?: { arrivalAt: string | null; arrivalWithReliefsAt?: string | null; willMakeIt: boolean | null } | null;
  position?: { freeFrom: string } | null;
}

const time = (iso: string | null | undefined) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : null;
};

/** Wartość kolumny do sortowania; `null` = brak danych (zawsze na końcu). */
export function recommendationValue(r: SortableRecommendation, key: RecommendationSortKey): number | null {
  switch (key) {
    case 'feasibility':
      return r.feasibility ? FEASIBILITY_ORDER[r.feasibility.grade] : null;
    case 'access':
      return r.accessRoute?.km ?? r.accessDistance?.roadKm ?? null;
    case 'windowReserve':
      return r.windowReserve?.hours ?? null;
    case 'workReserve':
      return r.workReserve?.hours ?? null;
    case 'margin':
      return r.margin?.pln ?? null;
    case 'delivery': {
      // Przyjazd, który pokazuje kolumna — z ulgami, gdy tylko one ratują termin.
      const d = r.delivery;
      const reliefs = d?.willMakeIt === false && !!d.arrivalWithReliefsAt;
      return time(reliefs ? d?.arrivalWithReliefsAt : d?.arrivalAt);
    }
    case 'freeFrom':
      return time(r.position?.freeFrom);
  }
}

/**
 * Lista posortowana po kolumnie: brak danych zawsze na końcu, przy równych
 * — kolejność wejściowa (sortowanie stabilne). `null` = bez zmian.
 */
export function sortRecommendations<T extends SortableRecommendation>(
  rows: readonly T[],
  sort: RecommendationSort | null,
): T[] {
  if (sort === null) return [...rows];
  const asc = BEST_ASCENDING[sort.key] !== sort.reversed;
  return rows
    .map((row, index) => ({ row, index, v: recommendationValue(row, sort.key) }))
    .sort((a, b) => {
      if (a.v === null && b.v === null) return a.index - b.index;
      if (a.v === null) return 1;
      if (b.v === null) return -1;
      return (asc ? a.v - b.v : b.v - a.v) || a.index - b.index;
    })
    .map((x) => x.row);
}

/** Opis kolumny w nazwie przycisku sortowania. */
const SORT_NAMES: Record<RecommendationSortKey, string> = {
  feasibility: 'wykonalności',
  access: 'dolotu',
  windowReserve: 'zapasu do okna',
  workReserve: 'zapasu czasu pracy',
  margin: 'marży',
  delivery: 'przyjazdu na rozładunek',
  freeFrom: '„wolny od”',
};

/**
 * Nagłówek z sortowaniem — bez `onSort` (przełącznik wyłączony, tabela
 * wariantów) zwykły tekst. Strzałka: od najlepszych / odwrotnie.
 *
 * `hint` — opis kolumny w dymku przed zdaniem o sortowaniu (15.10.2026,
 * krótkie podpisy „Zapasu”): podpis bywa skrótem, a pełna nazwa i opis
 * stoją w dymku; bez sortowania dymek z samym opisem, dostępny z klawiatury.
 */
export function SortableLabel({
  sortKey,
  sort,
  onSort,
  children,
  hint,
}: {
  sortKey: RecommendationSortKey;
  sort?: RecommendationSort | null;
  onSort?: (key: RecommendationSortKey) => void;
  children: ReactNode;
  hint?: string;
}) {
  if (!onSort) {
    if (!hint) return <>{children}</>;
    return (
      <Tooltip describeChild title={hint}>
        <Box component="span" tabIndex={0} sx={{ cursor: 'help' }}>
          {children}
        </Box>
      </Tooltip>
    );
  }
  const active = sort?.key === sortKey;
  const asc = BEST_ASCENDING[sortKey] !== (active && sort?.reversed === true);
  const sortText = `Sortuj według ${SORT_NAMES[sortKey]} — kliknij ponownie, aby odwrócić, trzeci raz, aby wrócić do kolejności domyślnej. Tylko widok tej listy.`;
  return (
    <Tooltip describeChild title={hint ? `${hint} ${sortText}` : sortText}>
      <TableSortLabel
        active={active}
        direction={asc ? 'asc' : 'desc'}
        onClick={() => onSort(sortKey)}
        data-testid={`sort-${sortKey}`}
      >
        {children}
      </TableSortLabel>
    </Tooltip>
  );
}

// ── Godzina przeliczenia ──────────────────────────────────────────────

/** „Przeliczono 15.10, 14:32” obok przycisku przeliczenia. */
export function ComputedAtNote({ at }: { at: string | null }) {
  if (!at) return null;
  return (
    <Tooltip title={`Rekomendacje policzono ${fullDateTime(at)}. Nie przeliczają się same — dolot z map i opłaty kosztują; użyj „Przelicz ponownie”.`}>
      <Typography variant="caption" color="text.secondary" sx={NOWRAP} data-testid="computed-at">
        {`Przeliczono ${shortDateTime(at)}`}
      </Typography>
    </Tooltip>
  );
}

// ── Lista pominiętych z powodem ───────────────────────────────────────

/** Lista „numer — powód” pod podsumowaniem pominiętych. */
export function SkippedReasonList({ items }: { items: Array<{ key: string | number; label: string; reason: string }> }) {
  if (items.length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }} data-testid="skipped-reasons">
      {items.map((i) => (
        <Typography key={i.key} component="li" variant="caption" color="text.secondary">
          <strong>{i.label}</strong> — {i.reason}
        </Typography>
      ))}
    </Box>
  );
}
